import { Close } from '@mui/icons-material';
import {
  Checkbox,
  Chip,
  Collapse,
  FormControl,
  InputLabel,
  ListItemText,
  MenuItem,
  Select,
  Stack,
} from '@mui/material';
import { translateKey } from 'src/locales/translate';
import { GetProfileMedicationsDtoStatusEnum } from 'woofwoof-api';
import { MEDICATION_STATUS_OPTIONS } from './medication-form/medicationFormOptions';

export type MedicationStatusValue = GetProfileMedicationsDtoStatusEnum;

export interface MedicationFilters {
  statuses: MedicationStatusValue[];
}

/**
 * What a patient is on, and what is lined up to start — the two a vet opening
 * the card is asking about. Paused and stopped courses are a click away.
 */
export const DEFAULT_MEDICATION_FILTERS: MedicationFilters = {
  statuses: ['ACTIVE', 'SCHEDULED'],
};

export const EMPTY_MEDICATION_FILTERS: MedicationFilters = { statuses: [] };

interface Props {
  value: MedicationFilters;
  onChange: (next: MedicationFilters) => void;
  isOpen: boolean;
}

/**
 * Status filter for a patient's medications: a panel that folds away, with the
 * active filters as chips below it.
 *
 * The chips sit outside the panel, as they do in the visit history: closing the
 * panel must not hide which statuses are still narrowing the table. This table
 * opens with two statuses already chosen, so that band is visible from the
 * start — which is the point, since a filtered table that looks unfiltered is
 * the worse trade.
 */
export default function ProfileMedicationsFilters({
  value,
  onChange,
  isOpen,
}: Props) {
  const statusLabel = (status: MedicationStatusValue) =>
    MEDICATION_STATUS_OPTIONS.find((option) => option.value === status)
      ?.label ?? status;

  const removeStatus = (status: MedicationStatusValue) =>
    onChange({
      statuses: value.statuses.filter((current) => current !== status),
    });

  return (
    <>
      {/* The card is a fixed-height flex column, so without this the filter row
        is compressed to fit and its controls clipped. */}
      <Collapse in={isOpen} unmountOnExit sx={{ flexShrink: 0 }}>
        <Stack sx={{ px: 2, pt: 2, gap: 2 }}>
          <FormControl size="small" fullWidth>
            <InputLabel id="profile-medications-status-label">
              {translateKey('medication.form.status')}
            </InputLabel>
            <Select
              multiple
              labelId="profile-medications-status-label"
              label={translateKey('medication.form.status')}
              value={value.statuses}
              data-testid="medication-status-filter"
              onChange={(event) =>
                onChange({
                  statuses: event.target.value as MedicationStatusValue[],
                })
              }
              // Nothing selected shows nothing: an empty filter already means
              // everything, and saying so takes a line to repeat it.
              renderValue={(selected) => selected.map(statusLabel).join(', ')}
            >
              {MEDICATION_STATUS_OPTIONS.map((option) => (
                <MenuItem
                  key={option.value}
                  value={option.value}
                  data-testid={`medication-filter-${option.value}`}
                >
                  <Checkbox checked={value.statuses.includes(option.value)} />
                  <ListItemText primary={option.label} />
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Stack>
      </Collapse>

      {/* Below the form, and outside the Collapse: they read as the result of
          the controls above them, and closing the panel must not hide which
          filters are still narrowing the table. */}
      {value.statuses.length > 0 && (
        <Stack
          direction="row"
          spacing={1}
          flexWrap="wrap"
          gap={1}
          // The gap above the chips comes from here alone. If the form kept a
          // bottom padding too, opening the panel would add the two together
          // and the chips would sit lower than they do when it is closed.
          sx={{ p: 2, flexShrink: 0, justifyContent: 'flex-end' }}
        >
          {value.statuses.map((status) => (
            <Chip
              key={status}
              label={statusLabel(status)}
              size="small"
              color="primary"
              variant="outlined"
              onDelete={() => removeStatus(status)}
              deleteIcon={<Close />}
            />
          ))}
          <Chip
            label={translateKey('visit.filtersClearAll')}
            size="small"
            variant="outlined"
            onClick={() => onChange(EMPTY_MEDICATION_FILTERS)}
            onDelete={() => onChange(EMPTY_MEDICATION_FILTERS)}
            deleteIcon={<Close />}
          />
        </Stack>
      )}
    </>
  );
}
